import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { User, LogOut, Briefcase, LayoutDashboard, MessageSquare, ChevronDown, Bell, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Navbar = ({ transparent = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingApplications, setPendingApplications] = useState(0);

  // Fetch notification counts
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;
      
      try {
        // Fetch unread messages count
        const msgRes = await axios.get(`${API}/messages/unread-count`);
        setUnreadMessages(msgRes.data.count || 0);
        
        // For companies, fetch pending applications count
        if (user.user_type === "company") {
          const appsRes = await axios.get(`${API}/company/applications`);
          const pending = appsRes.data.filter(app => app.status === "pending").length;
          setPendingApplications(pending);
        }
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    };

    fetchNotifications();
    // Refresh every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const totalNotifications = unreadMessages + pendingApplications;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getDashboardLink = () => {
    if (!user) return "/login";
    switch (user.user_type) {
      case "candidate": return "/dashboard/candidate";
      case "company": return "/dashboard/company";
      case "service_provider": return "/dashboard/provider";
      case "admin": return "/admin";
      default: return "/";
    }
  };

  const getUserTypeLabel = () => {
    if (!user) return "";
    switch (user.user_type) {
      case "candidate": return "Candidato";
      case "company": return "Azienda";
      case "service_provider": return "Fornitore";
      case "admin": return "Admin";
      default: return "";
    }
  };

  const needsSubscription = user && (user.user_type === "candidate" || user.user_type === "service_provider");

  const navClasses = transparent
    ? "absolute top-0 left-0 right-0 z-50 bg-transparent"
    : "sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200";

  const textClasses = transparent ? "text-white" : "text-slate-700";
  const logoClasses = transparent ? "text-white" : "text-[#DA291C]";

  return (
    <nav className={navClasses} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo + Brand */}
          <Link 
            to="/" 
            className="flex items-center gap-2 sm:gap-3"
            data-testid="navbar-logo"
          >
            <img 
              src="/logo-transparent.png" 
              alt="LavoroTicino Logo" 
              className="h-8 sm:h-10 w-auto"
            />
            <span className={`font-black text-lg sm:text-xl tracking-tight ${logoClasses}`}>
              Lavoro<span className={transparent ? "text-white" : "text-[#004E64]"}>Ticino</span>
            </span>
          </Link>

          {/* Desktop Navigation - Hidden on mobile */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/jobs" 
              className={`font-medium hover:opacity-80 transition-opacity ${textClasses}`}
              data-testid="nav-jobs"
            >
              <span translate="no">Annunci lavoro</span>
            </Link>
            <Link 
              to="/services" 
              className={`font-medium hover:opacity-80 transition-opacity ${textClasses}`}
              data-testid="nav-services"
            >
              <span translate="no">Offro servizi</span>
            </Link>
            <Link 
              to="/candidates" 
              className={`font-medium hover:opacity-80 transition-opacity ${textClasses}`}
              data-testid="nav-candidates"
            >
              <span translate="no">Candidati</span>
            </Link>

            {user ? (
              <div className="flex items-center gap-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant={transparent ? "ghost" : "outline"} 
                      className={`gap-2 relative ${transparent ? "text-white border-white/30 hover:bg-white/10" : ""}`}
                      data-testid="user-menu-trigger"
                    >
                      <User className="w-4 h-4" />
                      {user.name}
                      <span className="text-xs opacity-60">({getUserTypeLabel()})</span>
                      <ChevronDown className="w-3 h-3" />
                      {totalNotifications > 0 && (
                        <span className="absolute -top-1 -right-1 bg-[#DA291C] text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                          {totalNotifications > 99 ? "99+" : totalNotifications}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem asChild>
                      <Link to={getDashboardLink()} className="cursor-pointer" data-testid="dropdown-dashboard">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/messages" className="cursor-pointer flex items-center justify-between" data-testid="dropdown-messages">
                        <div className="flex items-center">
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Messaggi
                        </div>
                        {unreadMessages > 0 && (
                          <Badge className="bg-[#DA291C] text-white text-xs ml-2">
                            {unreadMessages}
                          </Badge>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    {user.user_type === "company" && pendingApplications > 0 && (
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard/company" className="cursor-pointer flex items-center justify-between" data-testid="dropdown-applications">
                          <div className="flex items-center">
                            <Users className="w-4 h-4 mr-2" />
                            Candidature
                          </div>
                          <Badge className="bg-emerald-600 text-white text-xs ml-2">
                            {pendingApplications} nuove
                          </Badge>
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {needsSubscription && (
                      <DropdownMenuItem asChild>
                        <Link to="/subscription" className="cursor-pointer" data-testid="dropdown-subscription">
                          <Briefcase className="w-4 h-4 mr-2" />
                          Abbonamento
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout} 
                      className="cursor-pointer text-red-600"
                      data-testid="dropdown-logout"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Esci
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button 
                    variant={transparent ? "ghost" : "outline"}
                    className={transparent ? "text-white border-white/30 hover:bg-white/10" : ""}
                    data-testid="nav-login"
                  >
                    Accedi
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="bg-[#DA291C] hover:bg-[#B91C1C] text-white"
                    data-testid="nav-register"
                  >
                    Registrati
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Navigation - Always visible, no hamburger */}
          <div className="flex md:hidden items-center gap-2">
            {user ? (
              /* Logged in - Compact dropdown */
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant={transparent ? "ghost" : "outline"} 
                    size="sm"
                    className={`gap-1 px-2 relative ${transparent ? "text-white border-white/30 hover:bg-white/10" : ""}`}
                    data-testid="mobile-user-menu"
                  >
                    <User className="w-4 h-4" />
                    <span className="max-w-[80px] truncate text-sm">{user.name.split(" ")[0]}</span>
                    <ChevronDown className="w-3 h-3" />
                    {totalNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 bg-[#DA291C] text-white text-xs font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-0.5 text-[10px]">
                        {totalNotifications > 99 ? "99+" : totalNotifications}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-sm font-medium text-slate-500">
                    {getUserTypeLabel()}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()} className="cursor-pointer">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/jobs" className="cursor-pointer">
                      <Briefcase className="w-4 h-4 mr-2" />
                      Annunci lavoro
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/messages" className="cursor-pointer flex items-center justify-between">
                      <div className="flex items-center">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Messaggi
                      </div>
                      {unreadMessages > 0 && (
                        <Badge className="bg-[#DA291C] text-white text-xs">
                          {unreadMessages}
                        </Badge>
                      )}
                    </Link>
                  </DropdownMenuItem>
                  {user.user_type === "company" && pendingApplications > 0 && (
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard/company" className="cursor-pointer flex items-center justify-between">
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-2" />
                          Candidature
                        </div>
                        <Badge className="bg-emerald-600 text-white text-xs">
                          {pendingApplications}
                        </Badge>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {needsSubscription && (
                    <DropdownMenuItem asChild>
                      <Link to="/subscription" className="cursor-pointer">
                        <Briefcase className="w-4 h-4 mr-2" />
                        Abbonamento
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout} 
                    className="cursor-pointer text-red-600"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              /* Not logged in - Direct buttons, no hamburger */
              <>
                <Link to="/login">
                  <span 
                    className={`text-sm font-medium hover:underline ${transparent ? "text-white" : "text-[#004E64]"}`}
                    data-testid="mobile-login"
                  >
                    Accedi
                  </span>
                </Link>
                <Link to="/register">
                  <Button 
                    size="sm"
                    className="bg-[#DA291C] hover:bg-[#B91C1C] text-white text-sm px-3 h-8"
                    data-testid="mobile-register"
                  >
                    Registrati
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
